import * as React from "react";
import useSWR from "swr";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Pagination from "@mui/material/Pagination";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import { getFaqListApi, createFaqApi, updateFaqApi, deleteFaqApi } from "src/api/faq/faqApi";
import type { FaqItem } from "src/store/faq";
import { useFaqPage, useFaqSize, useFaqOrder, useSetFaqQuery } from "src/store/faq";

// ✅ 너 프로젝트에 이미 있는 컴포넌트 그대로 사용
import FaqUpsertDialog, { FaqUpsertPayload } from "./FaqUpsertDialog";
import FaqTable from "./FaqTable";
import { snackbarStore } from "src/store/snackbarStore";

const CommonFaqView = () => {
  const page = useFaqPage(); // UI 1-based
  const size = useFaqSize();
  const order = useFaqOrder();
  const setQuery = useSetFaqQuery();

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editItem, setEditItem] = React.useState<FaqItem | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState<number | null>(null);

  const swrKey = React.useMemo(() => ["faq", page, size, order] as const, [page, size, order]);

  const { data, error, isValidating, mutate } = useSWR(
    swrKey,
    async () => {
      const apiPage = Math.max(0, page - 1);
      return await getFaqListApi({ page: apiPage, size, order });
    },
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  const list = data?.data.content ?? [];
  const totalPages = Math.max(1, data?.data.page.totalPages ?? 1);
  const totalElements = data?.data.page.totalElements ?? 0;

  const openCreate = () => {
    setEditItem(null);
    setDialogOpen(true);
  };

  const openEdit = (item: FaqItem) => {
    setEditItem(item);
    setDialogOpen(true);
  };

  const handleSubmit = async (payload: FaqUpsertPayload): Promise<void> => {
    try {
      setSaving(true);

      const res = payload.faqId ? await updateFaqApi(payload) : await createFaqApi(payload);

      if (res.statusCode !== 1) {
        snackbarStore.getState().addSnackbar({
          message: res.message || "처리에 실패했습니다.",
          severity: "error",
        });
        return;
      }

      // ✅ 성공 스낵바
      snackbarStore.getState().addSnackbar({
        message: res.message || "저장되었습니다.",
        severity: "success",
      });

      setDialogOpen(false);
      setEditItem(null);
      await mutate();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: FaqItem) => {
    const ok = window.confirm(`삭제할까요?\n\n[${item.faqType}] ${item.faqTtl}`);
    if (!ok) return;

    try {
      setDeletingId(item.faqId);

      const res = await deleteFaqApi({ faqId: item.faqId });

      if (res.statusCode !== 1) {
        snackbarStore.getState().addSnackbar({
          message: res.message || "삭제에 실패했습니다.",
          severity: "error",
        });
        return;
      }

      snackbarStore.getState().addSnackbar({
        message: res.message || "삭제 완료",
        severity: "success",
      });

      await mutate();
    } finally {
      setDeletingId(null);
    }
  };
  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
        <Typography variant="h6" sx={{ fontWeight: 900 }}>
          자주 묻는 질문 관리
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <Button variant="contained" onClick={openCreate}>
            + 새 FAQ 등록
          </Button>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="faq-sort-label">정렬</InputLabel>
            <Select
              labelId="faq-sort-label"
              label="정렬"
              value={order ?? "faqOrder,asc"}
              onChange={(e) => setQuery({ order: e.target.value, page: 1 })}
            >
              <MenuItem value="faqOrder,asc">순서 오름차순</MenuItem>
              <MenuItem value="faqOrder,desc">순서 내림차순</MenuItem>
              <MenuItem value="faqId,desc">ID 내림차순</MenuItem>
              <MenuItem value="faqId,asc">ID 오름차순</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="faq-size-label">개수</InputLabel>
            <Select
              labelId="faq-size-label"
              label="개수"
              value={String(size)}
              onChange={(e) => setQuery({ size: Number(e.target.value), page: 1 })}
            >
              <MenuItem value="10">10개</MenuItem>
              <MenuItem value="20">20개</MenuItem>
              <MenuItem value="50">50개</MenuItem>
            </Select>
          </FormControl>

          <Typography sx={{ fontSize: 13, fontWeight: 800, color: "text.secondary" }}>
            총 {totalElements}건
          </Typography>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {error ? (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {(error as any)?.message ?? "FAQ 목록 로드 실패"}
          </Alert>
        ) : null}

        {isValidating ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <CircularProgress size={18} />
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>불러오는 중...</Typography>
          </Stack>
        ) : null}

        {/* ✅ 기존 FaqTable 그대로 사용 */}
        <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
          <Pagination
            count={totalPages}
            page={Math.min(page, totalPages)}
            onChange={(_, value) => setQuery({ page: value })}
            shape="rounded"
            showFirstButton
            showLastButton
            sx={{
              mb: 2,
              ml: "auto",
            }}
          />
        </Stack>
        <FaqTable
          list={list}
          loading={false}
          onEdit={openEdit}
          onDelete={handleDelete}
          deletingKey={deletingId ? String(deletingId) : null}
        />
      </Paper>

      <FaqUpsertDialog
        open={dialogOpen}
        item={editItem}
        loading={saving}
        onClose={() => {
          setDialogOpen(false);
          setEditItem(null);
        }}
        onSubmit={handleSubmit}
      />
    </Box>
  );
};

export default CommonFaqView;
